import React, { useEffect, useRef } from 'react';

import { CustomLoggerProvider, useCustomLogger } from '@vp/pdp-ubik-logging';
import {
  Box,
  Collapsible,
  CollapsibleContent,
  CollapsibleSummary,
  CollapsibleSummaryButton,
  CollapsibleSummaryTextClosed,
  CollapsibleSummaryTextOpen,
  ScreenClassProvider,
  SWAN_STYLE_KEY_MAP,
  useScreenClass,
} from '@vp/swan';
import {
  IdentityProvider,
  useSwanStyleKeys,
  useUserContext,
  useUserContextTracker,
} from '@vp/ubik-context';
import {
  LocalizationsProvider,
  NestedStringObject,
  useLocalizations,
} from '@vp/ubik-localizations-provider';

import { Experiments } from '../constants/Experiments';
import { FlyoutProps } from '../types/types';

import { TenantMappingsProvider } from './contexts/TenantMappingsContext';
import { DesignServicesSwanStyleKeys, DesignServicesTile } from './design-services';
import { DesignServicesWrapperProps } from './design-services/components/DesignServicesTile';
import { FlyoutContainer, FlyoutContainerProps } from './FlyoutContainer';
import { shouldRender as shouldRenderMailingServices } from './mailing-services';
import { PaypalMessageSection } from './paypal/PaypalMessageSection';
import {
  FinishingOptionsSwanStylesKeys,
  shouldRender as shouldRenderFinishingOptions,
} from './product-finishing-options';
import { shouldRender as shouldRenderSampleFlyout } from './product-samples/components/Samples';
import { shouldRender as shouldRenderSizeDimensions } from './size-dimensions';
import {
  shouldRender as shouldRenderSpecsAndTemplates,
  SpecsTemplatesSwanStyleKeys,
} from './specs-and-templates';

export interface FragmentProps {
  locale: string;
  tenant: string;
  mpvId: string;
  flyoutProps: FlyoutProps;
  localizations: NestedStringObject;
  cartTenantId: string;
  defaultCartTypeId: string;
  environment?: string;
  merchant: string;
  account: string;
  isSocialSource: boolean;
}

export const Fragment: React.FC<FragmentProps> = (props) => {
  useSwanStyleKeys([
    SWAN_STYLE_KEY_MAP.accordion,
    ...SpecsTemplatesSwanStyleKeys,
    ...FinishingOptionsSwanStylesKeys,
    ...DesignServicesSwanStyleKeys,
  ]);

  return (
    <ScreenClassProvider>
      <IdentityProvider auth={{ culture: props.locale, developmentMode: false }}>
        <CustomLoggerProvider
          context={{ mpvId: props.mpvId, locale: props.locale, environment: props.environment }}
        >
          <WrappedFragment {...props} />
        </CustomLoggerProvider>
      </IdentityProvider>
    </ScreenClassProvider>
  );
};

const WrappedFragment: React.FC<FragmentProps> = (props) => {
  const { logger } = useCustomLogger();
  const screenClass = useScreenClass();
  const tracker = useUserContextTracker();
  const hasTrackedRef = useRef(false);
  const { experiments } = useUserContext();

  const recsExperimentVariation = experiments?.[Experiments.RECS_HIGHER_ON_MOBILE];

  useEffect(() => {
    if (
      screenClass === 'xs' &&
      recsExperimentVariation &&
      !hasTrackedRef.current &&
      props.isSocialSource
    ) {
      hasTrackedRef.current = true;
      tracker({
        type: 'experiment',
        name: Experiments.RECS_HIGHER_ON_MOBILE,
        variation: recsExperimentVariation,
      });
    }
  }, [screenClass, recsExperimentVariation, tracker]);

  const isMobileRecsExperiment = recsExperimentVariation === 'test';

  const designServicesProps: DesignServicesWrapperProps = {
    productKey: props.flyoutProps.productKey,
    mpvId: props.mpvId,
    locale: props.locale,
    data: props.flyoutProps.designServicesProps.data,
  };

  const flyoutContainerProps = buildFlyoutContainerProps(props);
  const numberOfFlyouts = countFlyouts(flyoutContainerProps);

  return (
    <LocalizationsProvider logger={logger} localizations={props.localizations}>
      <TenantMappingsProvider
        merchant={props.merchant}
        account={props.account}
        defaultCartTypeId={props.defaultCartTypeId}
      >
        {isMobileRecsExperiment &&
        props.isSocialSource &&
        screenClass === 'xs' &&
        numberOfFlyouts > 1 ? (
          <CollapsibleFragment flyoutContainerProps={flyoutContainerProps} />
        ) : (
          <Box>
            <DesignServicesTile {...designServicesProps} />
            <FlyoutContainer {...flyoutContainerProps} />
          </Box>
        )}
        <PaypalMessageSection
          locale={props.locale}
          cartTenantId={props.cartTenantId}
          mpvId={props.mpvId}
          productKey={props.flyoutProps.productKey}
        />
      </TenantMappingsProvider>
    </LocalizationsProvider>
  );
};

interface CollapsibleFragmentProps {
  flyoutContainerProps: FlyoutContainerProps;
}

const CollapsibleFragment: React.FC<CollapsibleFragmentProps> = ({ flyoutContainerProps }) => {
  const { t } = useLocalizations();

  return (
    <Collapsible headingLevel="h2">
      <CollapsibleSummary>
        <CollapsibleSummaryButton>
          <CollapsibleSummaryTextOpen>{t('less-details')}</CollapsibleSummaryTextOpen>
          <CollapsibleSummaryTextClosed>{t('more-details')}</CollapsibleSummaryTextClosed>
        </CollapsibleSummaryButton>
      </CollapsibleSummary>
      <CollapsibleContent>
        <FlyoutContainer {...flyoutContainerProps} />
      </CollapsibleContent>
    </Collapsible>
  );
};

const buildFlyoutContainerProps = (props: FragmentProps): FlyoutContainerProps => {
  return {
    specsAndTemplatesProps: {
      productKey: props.flyoutProps.productKey,
      productVersion: props.flyoutProps.productVersion,
      locale: props.locale,
      data: props.flyoutProps.specsAndTemplatesProps.data,
      mpvId: props.mpvId,
    },
    sizeDimensionsProps: {
      productKey: props.flyoutProps.productKey,
      data: props.flyoutProps.sizeAndDimensionsProps.data,
    },
    finishingOptionsProps: {
      productKey: props.flyoutProps.productKey,
      data: props.flyoutProps.finishingOptionsProps.data,
    },
    sampleProps: {
      tenant: props.tenant,
      cartTenant: props.cartTenantId,
      locale: props.locale,
      productInformation: props.flyoutProps.sampleProps.data,
    },
    mailingServicesProps: {
      productKey: props.flyoutProps.productKey,
      data: props.flyoutProps.mailingServicesContentProps.data,
      locale: props.locale,
    },
  };
};

const countFlyouts = (flyoutContainerProps: FlyoutContainerProps): number => {
  let flyoutCount = 0;

  if (shouldRenderSpecsAndTemplates(flyoutContainerProps.specsAndTemplatesProps)) flyoutCount++;
  if (shouldRenderSizeDimensions(flyoutContainerProps.sizeDimensionsProps)) flyoutCount++;
  if (shouldRenderFinishingOptions(flyoutContainerProps.finishingOptionsProps)) flyoutCount++;
  if (shouldRenderSampleFlyout(flyoutContainerProps.sampleProps)) flyoutCount++;
  if (shouldRenderMailingServices(flyoutContainerProps.mailingServicesProps)) flyoutCount++;

  return flyoutCount;
};
